// Inline SVG icon set — single source of truth, no icon-font dependency.

import type { SVGProps } from 'react';

type IconProps = { size?: number; className?: string; animate?: boolean };

function svg(size: number, className?: string): SVGProps<SVGSVGElement> {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    'aria-hidden': true,
    ...(className ? { className } : {}),
  };
}

export const MenuIcon = ({ size = 20, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

export const ChevronLeftIcon = ({ size = 18, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

export const ChevronRightIcon = ({ size = 14, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export const ChevronDownIcon = ({ size = 18, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

export const SettingsIcon = ({ size = 18, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z" />
  </svg>
);

export const SendIcon = ({ size = 18, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <line x1="12" y1="19" x2="12" y2="5" />
    <polyline points="5 12 12 5 19 12" />
  </svg>
);

export const PaperclipIcon = ({ size = 18, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <path d="m21 11-9.5 9.5a5 5 0 1 1-7-7L13 4a3.5 3.5 0 1 1 5 5l-9.5 9.5a2 2 0 1 1-3-3L14 7" />
  </svg>
);

export const FilePlusIcon = ({ size = 56, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="12" y1="12" x2="12" y2="18" />
    <line x1="9" y1="15" x2="15" y2="15" />
  </svg>
);

export const CloseIcon = ({ size = 18, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

export const CheckIcon = ({ size = 14, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

export const RetryIcon = ({ size = 12, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <polyline points="23 4 23 10 17 10" />
    <path d="M20.49 15A9 9 0 1 1 18 5.34L23 10" />
  </svg>
);

export const ArrowDownIcon = ({ size = 18, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <polyline points="19 12 12 19 5 12" />
  </svg>
);

export const FileIcon = ({ size = 14, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

// Diamond kite spinner — 8 faceted kites radially arranged.
// Colors via currentColor + fillOpacity; no theme-specific values needed.
const KITE = {
  tip:   '12,2.5',
  right: '13.5,3.7',
  inner: '12,9.5',
  left:  '10.5,3.7',
  ridge: '12.3,4.28', // ridge X offset 0.3, Y at ~10% of shoulder→inner
} as const;

const WAVE_DURATION = 2.4;

function DiamondKite({ angle, animIndex }: { angle: number; animIndex?: number }) {
  const { tip, right, inner, left, ridge } = KITE;
  const waveStyle = animIndex !== undefined ? {
    animation: `diamond-wave ${WAVE_DURATION}s ease-in-out infinite`,
    animationDelay: `${-((animIndex / 8) * WAVE_DURATION).toFixed(2)}s`,
    transformBox: 'fill-box' as const,
    transformOrigin: 'center',
  } : undefined;
  return (
    <g transform={`rotate(${angle},12,12)`}>
      <g style={waveStyle}>
        <polygon points={`${tip} ${right} ${ridge}`}  fill="currentColor" fillOpacity={1.00} />
        <polygon points={`${ridge} ${right} ${inner}`} fill="currentColor" fillOpacity={0.65} />
        <polygon points={`${tip} ${left} ${ridge}`}   fill="currentColor" fillOpacity={0.42} />
        <polygon points={`${ridge} ${left} ${inner}`}  fill="currentColor" fillOpacity={0.18} />
      </g>
    </g>
  );
}

export const SparklesIcon = ({ size = 14, className, animate }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" {...(className ? { className } : {})}>
    {[0, 45, 90, 135, 180, 225, 270, 315].map((a, i) => (
      <DiamondKite key={a} angle={a} animIndex={animate ? i : undefined} />
    ))}
  </svg>
);

export const WrenchIcon = ({ size = 14, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3 18l3 3 6.3-6.3a4 4 0 0 0 5.4-5.4l-2.5 2.5-2.5-2.5 2.5-2.5z" />
  </svg>
);

export const AlertIcon = ({ size = 16, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

export const SunIcon = ({ size = 16, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" />
  </svg>
);

export const MoonIcon = ({ size = 16, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
  </svg>
);

export const MonitorIcon = ({ size = 16, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
  </svg>
);

export const ReloadIcon = ({ size = 15, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    <polyline points="21 3 21 9 15 9" />
  </svg>
);

export const WindowIcon = ({ size = 16, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
  </svg>
);

export const PinIcon = ({ size = 16, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <path d="M12 17v5" />
    <path d="M9 10.76V6a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4.76a2 2 0 0 0 .59 1.41l1.7 1.7A1 1 0 0 1 16.59 15H7.41a1 1 0 0 1-.7-1.71l1.7-1.7A2 2 0 0 0 9 10.76Z" />
  </svg>
);

export const EyeIcon = ({ size = 12, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

export const EyeOffIcon = ({ size = 12, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
    <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
    <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

export const PinOffIcon = ({ size = 16, className }: IconProps) => (
  <svg {...svg(size, className)}>
    <path d="M3 3l18 18" />
    <path d="M12 17v5" />
    <path d="M15 10.76V6a1 1 0 0 0-1-1h-4M9 10.76V8.2M7.41 15h9.18a1 1 0 0 0 .7-1.71l-1.7-1.7" />
  </svg>
);
